import React, { useState, useEffect, useContext, useRef } from 'react';
import { UniverseBuilder } from '@baemingo/inventory-events';
import { useSelector, useDispatch, useLogger } from '~Hooks';
import { v4 as uuid } from 'uuid';

import * as actions from '~actions';

/**
 * Create a context with an empty universe. This context acts as a
 * state that we can access from child components. We use this in app.tsx
 */
const Context = React.createContext(new UniverseBuilder().serialize());

export const InventoryUniverseProvider: React.FunctionComponent = ({ children }) => {
  // A local universe
  const universe = useRef(new UniverseBuilder());

  // The current index of which event we've added to the universe from redux
  const index = useRef(0);

  // Get events from state. Events are added via UI actions or synced from backend
  const events = useSelector((state) => state.inventoryEvents);

  // Get snapshot from state. If this changes we override our current universe
  const snapshot = useSelector((state) => state.inventorySnapshot.snapshot);

  // We save the serialized universe in state to trigger re-renders
  const [state, setState] = useState(universe.current.serialize());

  const mountLogger = useLogger('mount');
  useEffect(() => {
    mountLogger.info('Mounted: InventoryUniverseProvider');
    return () => {
      mountLogger.info('Unmounted: InventoryUniverseProvider');
    };
  }, []);

  useEffect(() => {
    // Start by removing any events we've already added, then add the event
    events.slice(index.current).forEach((event) => universe.current.addEvent(event));

    // Update our active index
    index.current = events.length;

    // Save to state and trigger a re-render
    setState(universe.current.serialize());
  }, [events]);

  useEffect(() => {
    if (!snapshot) {
      return;
    }

    universe.current = new UniverseBuilder(snapshot);
    setState(universe.current.serialize());
  }, [snapshot]);

  return <Context.Provider value={state}>{children}</Context.Provider>;
};

export const useInventoryUniverse = () => {
  return useContext(Context);
};

export const useInventoryHelpers = () => {
  const universe = useInventoryUniverse();

  const getItemFromUniverse = (variationId: string) => {
    return universe.items.find((item) => item.itemId === variationId);
  };

  const itemIsOutOfStock = (variationId: string) => {
    const variation = getItemFromUniverse(variationId);
    return variation ? variation.outOfStock : false;
  };

  return {
    getItemFromUniverse,
    itemIsOutOfStock,
  };
};

export const useInventoryApi = (venueId: string | null) => {
  const companyId = useSelector((state) => state.company);
  const dispatch = useDispatch();

  const generateEventMeta = () => ({
    createdAt: new Date().toISOString(),
    eventId: uuid(),
    sentAt: null,
    venueId: venueId!,
    companyId,
  });

  const API = {
    addStock: (itemId: string, count: number) => {
      dispatch(
        actions.addInventoryEvent({
          type: 'ADD_STOCK',
          itemId,
          count,
          ...generateEventMeta(),
        }),
      );
    },

    reduceStock: (itemId: string, count: number, reason: string) => {
      dispatch(
        actions.addInventoryEvent({
          type: 'REDUCE_STOCK',
          itemId,
          count,
          reason,
          ...generateEventMeta(),
        }),
      );
    },

    clearInventory: (itemId: string) => {
      dispatch(
        actions.addInventoryEvent({
          type: 'CLEAR_INVENTORY',
          itemId,
          ...generateEventMeta(),
        }),
      );
    },

    moveInventory: (itemId: string, count: number, toVenueId: string) => {
      // Move inventory from the current venue
      // (this reduces inventory in current venue)
      dispatch(
        actions.addInventoryEvent({
          type: 'MOVE_INVENTORY',
          itemId,
          count,
          toVenue: toVenueId,
          ...generateEventMeta(),
        }),
      );

      // Add inventory to the selected venue
      dispatch(
        actions.addInventoryEvent({
          type: 'ADD_STOCK',
          itemId,
          count,
          ...generateEventMeta(),
          venueId: toVenueId,
        }),
      );
    },

    setOutOfStock: (itemId: string, outOfStock: boolean) => {
      dispatch(
        actions.addInventoryEvent({
          type: 'SET_OUT_OF_STOCK',
          itemId, // This can be any resource, variation, category etc
          isOutOfStock: outOfStock,
          ...generateEventMeta(),
        }),
      );
    },

    expireItems: (date: Date) => {
      dispatch(
        actions.addInventoryEvent({
          type: 'EXPIRE_ITEMS',
          timestamp: date.toISOString(),
          ...generateEventMeta(),
        }),
      );
    },

    addRequest: (date: Date, items: { itemId: string; count: number }[], comment: string) => {
      dispatch(
        actions.addInventoryEvent({
          type: 'PRODUCTION_REQUEST',
          timestamp: date.toISOString(),
          items,
          comment,
          requestId: uuid(),
          ...generateEventMeta(),
        }),
      );
    },

    removeRequest: (requestId: string) => {
      dispatch(
        actions.addInventoryEvent({
          type: 'REMOVE_PRODUCTION_REQUEST',
          requestId,
          ...generateEventMeta(),
        }),
      );
    },
  };

  return API;
};
